import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT = {
  faible: 'bg-green-500/20 text-green-300 border-green-500/50',
  moyen: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  eleve: 'bg-red-500/20 text-red-300 border-red-500/50',
};

const Block = ({ title, children }) => (
  <div>
    <p className="text-xs text-[#888680] uppercase font-bold mb-1">{title}</p>
    <div className="text-[#dfddd6] text-sm leading-relaxed">{children}</div>
  </div>
);

const ProspectAiAnalysis = ({ prospect, token, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const a = prospect.ai_analysis;

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/admin/ai/analyse-prospect/${prospect.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md:col-span-2 bg-[#1a1c1b] border border-[#3f4816] rounded-xl p-4 space-y-4" data-testid={`prospect-ai-${prospect.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#d9fb06]" />
          <h4 className="font-bold text-[#d9fb06] uppercase text-sm">Pré-analyse IA</h4>
          {a?.niveau_alerte && (
            <span className={`text-xs px-2 py-0.5 rounded-full border ${ALERT[a.niveau_alerte] || ALERT.moyen}`} data-testid={`prospect-ai-alert-${prospect.id}`}>
              Alerte {a.niveau_alerte}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid={`prospect-ai-btn-${prospect.id}`}
          className="flex items-center gap-2 text-xs border border-[#d9fb06] text-[#d9fb06] px-3 py-1.5 rounded-full hover:bg-[#d9fb06] hover:text-[#1a1c1b] disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {a?.resume ? 'Régénérer' : 'Lancer la pré-analyse'}
        </button>
      </div>

      {error && <p className="text-red-300 text-xs" data-testid={`prospect-ai-error-${prospect.id}`}>{error}</p>}
      {a?.error && !a.resume && (
        <p className="text-yellow-300 text-xs flex items-center gap-1"><AlertTriangle size={12} /> La génération automatique a échoué : {a.error}</p>
      )}
      {!a && !loading && <p className="text-[#888680] text-xs">Aucune pré-analyse pour l'instant (générée automatiquement à chaque nouvelle demande).</p>}
      {loading && !a?.resume && <p className="text-[#888680] text-xs">Génération en cours (10-20 s)…</p>}

      {a?.resume && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Block title="Résumé">{a.resume}</Block>
          <Block title="Cohérence du prix">{a.coherence_prix}</Block>
          <Block title="Points de vigilance">
            <ul className="list-disc pl-4 space-y-0.5">{(a.points_vigilance || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          </Block>
          <Block title="Questions à poser au vendeur">
            <ol className="list-decimal pl-4 space-y-0.5">{(a.questions_vendeur || []).map((x, i) => <li key={i}>{x}</li>)}</ol>
          </Block>
          <Block title="Adéquation projet">{a.adequation_projet}</Block>
          <Block title="Recommandation">{a.recommandation}</Block>
          <p className="md:col-span-2 text-[10px] text-[#888680]">
            Généré par {a.model} le {a.generated_at ? new Date(a.generated_at).toLocaleString('fr-FR') : '—'} · aide interne, à valider par l'expert avant toute communication au client.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProspectAiAnalysis;
